<template>
  <layout :drawerShow="true" :drawerWidth="350">
    <template slot="nav">
      <v-tabs>
        <v-tab>Meta Audience logs</v-tab>
      </v-tabs>
    </template>
    <template slot="nav-tools">
      <smart-btn
          secondary
          class="mr-6"
          @click.native="relauchExport"
      >
        Relancer
      </smart-btn>

      <smart-btn
          secondary
          class="mr-6"
          @click.native="checkExport"
      >
        Vérifier
      </smart-btn>
    </template>

    <template slot="drawer">
      <div class="pa-4">
        <v-row class="mb-2 mt-4">
          <v-select
              v-model="filters.segment"
              :items="segments"
              label="Segment"
              dense
              clearable
          />
        </v-row>

        <v-row class="mb-2">
          <v-select
              v-model="filters.status"
              :items="statuses"
              label="Status"
              dense
              clearable
          />
        </v-row>

        <v-row class="mb-2">
          <smart-picker
              v-model="filters.date"
              :inputOpts="{ label: 'Date d\'export' }"
          />
        </v-row>

        <smart-btn
            block
            primary
            class="mt-10"
            @click.native="search"
        >
          Rechercher
        </smart-btn>

        <smart-btn
            block
            tertiary
            class="mt-4"
            @click.native="loadData"
        >
          Réinitialiser la recherche
        </smart-btn>
      </div>
    </template>

    <template slot="content">
      <v-container fluid class="pa-6">
        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text>
                <div class="text-caption grey--text">
                  EXPORTS
                </div>
                <div class="text-h5 mt-1">
                  {{ summary.total }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text>
                <div class="text-caption grey--text">
                  RÉUSSIS
                </div>
                <div class="text-h5 mt-1">
                  {{ summary.success }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text>
                <div class="text-caption grey--text">
                  REÇUS
                </div>
                <div class="text-h5 mt-1">
                  {{ formatNumber(summary.received) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text>
                <div class="text-caption grey--text">
                  INVALIDES
                </div>
                <div class="text-h5 mt-1">
                  {{ formatNumber(summary.invalid) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined>
          <v-data-table
              class="tbl"
              :headers="headers"
              :items="exports"
              :loading="loading"
              :footer-props="{
                'items-per-page-options': [{text: 'Tous', value: -1}],
                'items-per-page-text': '',
              }"
              item-key="id"

              @click:row="openExport"
          >
            <template #item.started_at="{ item }">
              {{ formatDateTime(item.started_at) }}
            </template>

            <template #item.segment_label="{ item }">
              <v-chip
                  small
                  label
                  outlined
              >
                {{ item.segment_label }}
              </v-chip>
            </template>

            <template #item.num_received="{ item }">
              {{ formatNumber(item.num_received) }}
            </template>

            <template #item.num_invalid_entries="{ item }">
              <span
                  :class="{
                  'error--text': item.num_invalid_entries > 0
                }"
              >
                {{ formatNumber(item.num_invalid_entries) }}
              </span>
            </template>

            <template #item.validity="{ item }">
              {{ validity(item).toFixed(2) }} %
            </template>

            <template #item.operation_status="{ item }">
              <v-chip
                  small
                  label
                  :color="statusesColor[item.operation_status] || 'grey'"
                  text-color="white"
              >
                {{ statuses[item.operation_status] || 'unknown' }}
              </v-chip>
            </template>

            <template #item.checked_at="{ item }">
              <span v-if="item.checked_at">
                <v-icon
                    small
                    color="success"
                    class="mr-1"
                >
                  mdi-check-circle
                </v-icon>
                {{ formatTime(item.checked_at) }}
              </span>

              <span v-else class="grey--text">
                —
              </span>
            </template>
          </v-data-table>
        </v-card>

        <v-dialog
            v-model="detailDialog"
            max-width="600"
        >
          <v-card>
            <v-card-title>
              {{ selectedExport && selectedExport.segment_label }}
              <v-spacer />
              <v-btn icon @click="detailDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text v-if="detailLoading">
              <v-progress-linear indeterminate />
            </v-card-text>

            <v-card-text v-else-if="selectedExport">
              <v-simple-table dense>
                <tbody>
                <tr>
                  <td>Audience ID</td>
                  <td class="text-right">
                    {{ selectedExport.audience_id }}
                  </td>
                </tr>
                <tr>
                  <td>Session ID</td>
                  <td class="text-right">
                    {{ selectedExport.session_id }}
                  </td>
                </tr>
                <tr>
                  <td>Started</td>
                  <td class="text-right">
                    {{ formatDateTime(selectedExport.started_at) }}
                  </td>
                </tr>
                <tr>
                  <td>Received</td>
                  <td class="text-right">
                    {{ formatNumber(selectedExport.num_received) }}
                  </td>
                </tr>
                <tr>
                  <td>Invalid entries</td>
                  <td class="text-right">
                    {{ formatNumber(selectedExport.num_invalid_entries) }}
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td class="text-right">
                    <v-chip
                        small
                        label
                        :color="statusColor(selectedExport.operation_status)"
                        text-color="white"
                    >
                      {{ selectedExport.operation_status }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td>Checked at</td>
                  <td class="text-right">
                    {{ selectedExport.checked_at
                      ? formatDateTime(selectedExport.checked_at)
                      : "—"
                    }}
                  </td>
                </tr>
                </tbody>
              </v-simple-table>

              <template
                  v-if="
                  selectedExport.invalid_entry_samples &&
                  selectedExport.invalid_entry_samples.length
                "
              >
                <div class="text-subtitle-2 mt-6 mb-2">
                  Invalid entry samples
                </div>

                <v-list dense>
                  <v-list-item
                      v-for="(sample, index) in selectedExport.invalid_entry_samples"
                      :key="index"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ sample }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-container>
    </template>
  </layout>
</template>

<script>
import Layout from "@/views/Layout";
import {get} from "@/utils/api";

export default {
  name: "Meta",

  components: {
    Layout
  },

  data() {
    return {
      filters: {
        segment: null,
        status: null,
        date: null
      },

      segments: [
        "Solo",
        "Petit groupe",
        "Group",
        "Family"
      ],


      statuses: {
        0: 'unknown',    // Statut non disponible
        100: 'warning',  // Expiration programmée (inactive 2 ans, suppression après 90j)
        200: 'success',  // Normal, pas de mise à jour ni de souci
        400: 'warning',  // Avertissement générique
        410: 'warning',  // Aucun fichier uploadé
        411: 'warning',  // Faible taux de correspondance
        412: 'error',    // Trop d'entrées invalides dans le dernier upload
        414: 'pending',  // Remplacement en cours
        415: 'error',    // Échec du remplacement
        421: 'warning',  // Pas de pixel installé
        422: 'warning',  // Pixel installé mais ne remonte rien
        423: 'error',    // Pixel invalide
        431: 'error',    // Échec refresh lookalike
        432: 'error',    // Échec build lookalike
        433: 'error',    // Échec build lookalike
        434: 'pending',  // Build lookalike en retry
        441: 'success',  // Peuplement en cours mais déjà utilisable
        442: 'error',    // Échec du pré-remplissage
        450: 'warning',  // Audience obsolète (non utilisée depuis 30j+/jamais utilisée après 90j)
        470: 'error',    // Compte propriétaire désactivé
        471: 'error',    // Audience flaggée (intégrité)
        500: 'error'     // Erreur, action requise
      },
      statusesColor: {
        0: 'unknown',
        100: 'info',
        200: 'success',
        400: 'info',
        410: 'info',
        411: 'info',
        412: 'error',
        414: 'pending',
        415: 'error',
        421: 'info',
        422: 'info',
        423: 'error',
        431: 'error',
        432: 'error',
        433: 'error',
        434: 'pending',
        441: 'success',
        442: 'error',
        450: 'info',
        470: 'error',
        471: 'error',
        500: 'error'
      },

      headers: [
        {
          text: "Date",
          value: "started_at"
        },
        {
          text: "Segment",
          value: "segment_label"
        },
        {
          text: "Reçus",
          value: "num_received",
          align: "right"
        },
        {
          text: "Invalides",
          value: "num_invalid_entries",
          align: "right"
        },
        {
          text: "Validité",
          value: "validity",
          align: "right"
        },
        {
          text: "Statut",
          value: "operation_status"
        },
        {
          text: "Vérifié",
          value: "checked_at"
        }
      ],

      exports: [],
      total: 0,


      loading: false,

      detailDialog: false,
      detailLoading: false,
      selectedExport: null
    };
  },

  computed: {
    summary() {
      const received = this.exports.reduce(
          (sum, item) => sum + (item.num_received || 0),
          0
      );

      const invalid = this.exports.reduce(
          (sum, item) => sum + (item.num_invalid_entries || 0),
          0
      );

      return {
        total: this.total,
        success: this.exports.filter(
            item => item.operation_status === "Success"
        ).length,
        received,
        invalid
      };
    }
  },

  created() {
    this.loadData();
  },

  methods: {
    async search() {
      await this.loadData();
    },

    async loadData() {
      this.loading = true;

      try {
        await get("/api/bigquery/meta-audience-stat").json((rs) => {
          this.exports = rs
        });

        this.total = this.exports.length;
      } finally {
        this.loading = false;
      }
    },

    async relauchExport() {
      await get('/api/bigquery/run-meta-audience')
      await this.loadData()
    },


    async checkExport() {
      await get('/api/bigquery/check-meta-audience')
      await this.loadData()
    },

    async openExport(item) {
      this.detailDialog = true;
      this.detailLoading = true;
      this.selectedExport = null;

      try {

        //get data
        this.selectedExport = item
      } finally {
        this.detailLoading = false;
      }
    },

    formatNumber(value) {
      return new Intl.NumberFormat("fr-FR").format(value || 0);
    },

    formatTime(value) {
      return new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(value));
    },

    formatDateTime(value) {
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(value));
    },

    validity(item) {
      if (!item.num_received) {
        return 0;
      }

      return (
          (item.num_received - (item.num_invalid_entries || 0)) /
          item.num_received
      ) * 100;
    },
  }
};
</script>

<style scoped>
.text-no-wrap {
  white-space: nowrap;
}
</style>